import { useEffect } from "react";
import { Link } from "react-router-dom";
import type { CtaLink } from "../content/types";

const ORCID = "https://orcid.org/0009-0002-2878-732X";

const WHY = [
  {
    title: "Built at the point of action",
    body: "Our controls sit where AI systems call tools, move data and commit transactions, because that is where the risk is, not in the chat window.",
  },
  {
    title: "Evidence, not assertions",
    body: "Decisions are recorded write-once and signed, so an auditor, a regulator or a board can verify them without taking our word for it.",
  },
  {
    title: "Research in the open",
    body: "Our methods are published as preprints with DOIs, and the open-source cores let your engineers read the code before they trust it.",
  },
  {
    title: "Security and compliance, designed together",
    body: "One company builds both disciplines, so the security team and the compliance team are not reconciling two vendors' assumptions.",
  },
];

const DISCIPLINES = [
  {
    name: "AI Security",
    href: "/ai-security",
    blurb: "Runtime defense, adversarial testing and threat detection for autonomous agents.",
  },
  {
    name: "Governance & Compliance",
    href: "/governance",
    blurb: "Continuous compliance with evidence an auditor can verify, not a scramble once a year.",
  },
  {
    name: "Warden services",
    href: "/warden",
    blurb: "The engineers behind our products, applied to assessment, deployment and managed programs.",
  },
];

const TRUST: (CtaLink & { blurb: string })[] = [
  {
    label: "Trust Center",
    href: "https://trust.odingard.com",
    external: true,
    blurb: "Our own security posture, policies and documentation for your vendor review.",
  },
  {
    label: "Research library",
    href: "/research",
    blurb: "Every paper behind the products, with its DOI, method and stated limits.",
  },
  {
    label: "Open source",
    href: "/resources?type=code",
    blurb: "The cores and benchmarks we publish under open licenses.",
  },
];

const CONTACTS: (CtaLink & { blurb: string })[] = [
  {
    label: "Executive briefing",
    href: "/contact?topic=briefing",
    blurb: "A working session for security, risk and compliance leaders.",
  },
  {
    label: "Press",
    href: "/contact?topic=press",
    blurb: "Media inquiries, interviews and commentary on AI security.",
  },
  {
    label: "Investor inquiries",
    href: "/contact?topic=investors",
    blurb: "Conversations with the founder about the company.",
  },
  {
    label: "Research and disclosure",
    href: "/contact?topic=research",
    blurb: "Collaborate on research or report a vulnerability.",
  },
];

function CardLink({ item }: { item: CtaLink & { blurb: string } }) {
  const body = (
    <>
      <b>{item.label}</b>
      <span className="mm__blurb">{item.blurb}</span>
    </>
  );
  return item.external ? (
    <a href={item.href} className="mm__card">
      {body}
    </a>
  ) : (
    <Link to={item.href} className="mm__card">
      {body}
    </Link>
  );
}

/**
 * The company page answers who Odingard is, what it stands for and who leads
 * it. Product detail stays on the product pages.
 */
export function CompanyPage() {
  useEffect(() => {
    document.title = "Company — Odingard";
  }, []);

  return (
    <main className="page company" id="main">
      <section className="pillar__hero">
        <p className="page__eyebrow">Company</p>
        <h1>Security and compliance for AI that acts</h1>
        <p className="page__lede">
          Odingard Security builds the controls and the evidence that let
          enterprises put autonomous AI to work without losing control of it.
        </p>
      </section>

      <section className="pdp__section" id="mission">
        <h2>Our mission</h2>
        <p className="pdp__body">
          Make autonomous AI safe to deploy at enterprise scale. Every action
          an AI system takes should be checked against its authority at the
          moment it happens, and every decision should leave evidence that
          holds up in front of an auditor, a regulator or a board.
        </p>
      </section>

      <section className="pdp__section" id="why">
        <h2>Why Odingard</h2>
        <div className="co__why">
          {WHY.map((w) => (
            <article key={w.title}>
              <h3>{w.title}</h3>
              <p>{w.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="pdp__section" id="what-we-do">
        <h2>What we do</h2>
        <div className="co__cards">
          {DISCIPLINES.map((d) => (
            <Link key={d.href} to={d.href} className="mm__card">
              <b>{d.name}</b>
              <span className="mm__blurb">{d.blurb}</span>
            </Link>
          ))}
        </div>
      </section>

      <section className="pdp__section" id="leadership">
        <h2>Leadership</h2>
        <article className="co__leader">
          <p className="co__role">Founder</p>
          <h3>Andre Byrd</h3>
          <p>
            Andre founded Odingard Security to build runtime security and
            governance for autonomous AI agents. He leads the company&rsquo;s
            research and publishes it as open preprints.
          </p>
          <p className="co__links">
            <Link to="/research">Research</Link>
            <a href={ORCID}>ORCID</a>
          </p>
        </article>
      </section>

      <section className="pdp__section" id="trust">
        <h2>How we hold ourselves to account</h2>
        <div className="co__cards">
          {TRUST.map((t) => (
            <CardLink key={t.href} item={t} />
          ))}
        </div>
      </section>

      <section className="pdp__section" id="contact">
        <h2>Talk to the company</h2>
        <div className="co__cards">
          {CONTACTS.map((c) => (
            <CardLink key={c.href} item={c} />
          ))}
        </div>
      </section>

      <section className="pdp__close">
        <h2>Secure the action. Prove the outcome.</h2>
        <p className="pdp__body">
          Talk to the team about putting autonomous AI to work without losing
          control of it.
        </p>
        <div className="pdp__ctas">
          <Link className="btn btn--primary" to="/contact?topic=briefing">
            Request a briefing
          </Link>
          <Link className="btn btn--quiet" to="/contact">
            Contact us
          </Link>
        </div>
      </section>
    </main>
  );
}
